import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faSpinner, faChartLine, faClock, faPlug, faRefresh, faTriangleExclamation, faList, faKey, faCheckCircle, faTimesCircle } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { handleAPIError } from '@/utils/toasts';
import { formatDate, formatRFCDate, formatCurrencyCentValue, formatPercentage } from '@/utils/helpers';
import type { User, JournalOverview, JournalIBSyncRun, JournalIBSyncStatus, JournalIBSyncTrigger } from '@/models';

interface UserDetailPageTabJournalProps {
  user: User;
}

const SYNC_RUN_LIMIT = 5;

const SYNC_STATUS_LABELS: Record<JournalIBSyncStatus, string> = {
  NOT_CONFIGURED: 'Nicht eingerichtet',
  RUNNING: 'Läuft',
  SUCCESS: 'Erfolgreich',
  NO_DATA: 'Keine neuen Daten',
  RATE_LIMITED: 'Limit erreicht',
  UNAVAILABLE: 'Nicht erreichbar',
  FAILED: 'Fehlgeschlagen',
};

const SYNC_STATUS_CLASSES: Record<JournalIBSyncStatus, string> = {
  NOT_CONFIGURED: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  RUNNING: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-400',
  SUCCESS: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400',
  NO_DATA: 'bg-lime-100 text-lime-800 dark:bg-lime-900/50 dark:text-lime-400',
  RATE_LIMITED: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400',
  UNAVAILABLE: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400',
  FAILED: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400',
};

const SYNC_TRIGGER_LABELS: Record<JournalIBSyncTrigger, string> = {
  MANUAL: 'Manuell',
  JOB: 'Automatisch',
  FILE: 'Datei-Import',
};

function SyncStatusBadge({ status }: { status: JournalIBSyncStatus }) {
  return (
    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full whitespace-nowrap ${SYNC_STATUS_CLASSES[status]}`}>
      {SYNC_STATUS_LABELS[status]}
    </span>
  );
}

function BooleanBadge({ value, trueLabel, falseLabel }: { value: boolean; trueLabel: string; falseLabel: string }) {
  return (
    <span className={`inline-flex items-center px-3 py-1 text-sm font-semibold rounded-full ${
      value
        ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400'
        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
    }`}>
      <FontAwesomeIcon icon={value ? faCheckCircle : faTimesCircle} className="w-4 h-4 mr-2" />
      {value ? trueLabel : falseLabel}
    </span>
  );
}

function StatTile({ label, value, hint, valueClass }: { label: string; value: string; hint?: string; valueClass?: string }) {
  return (
    <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
      <div className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${valueClass ?? 'text-gray-900 dark:text-white'}`}>{value}</div>
      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 min-h-4">{hint ?? ''}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{label}</div>
      <div className="text-gray-900 dark:text-white">{children}</div>
    </div>
  );
}

const pnlColorClass = (cents: number): string => {
  if (cents > 0) return 'text-green-600 dark:text-green-400';
  if (cents < 0) return 'text-red-600 dark:text-red-400';
  return 'text-gray-900 dark:text-white';
};

const syncRunPeriod = (run: JournalIBSyncRun): string => {
  if (run.fromDate === null && run.toDate === null) return '—';
  const from = run.fromDate !== null ? formatRFCDate(run.fromDate) : '…';
  const to = run.toDate !== null ? formatRFCDate(run.toDate) : '…';
  return `${from} – ${to}`;
};

export default function UserDetailPageTabJournal({ user }: UserDetailPageTabJournalProps) {
  const journalEnabled = user.featureConfig.journal;

  const [loading, setLoading] = useState(journalEnabled);
  const [error, setError] = useState<string | null>(null);
  const [overview, setOverview] = useState<JournalOverview | null>(null);
  const [syncRuns, setSyncRuns] = useState<JournalIBSyncRun[]>([]);

  const fetchJournalData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [overviewData, runsData] = await Promise.all([
        APIRoutes.getJournalOverview(user.id),
        APIRoutes.listJournalIBSyncRuns({ limit: SYNC_RUN_LIMIT }, user.id),
      ]);

      setOverview(overviewData);
      setSyncRuns(runsData.runs);
    } catch (err) {
      console.error('Error loading journal overview:', err);
      setError('Fehler beim Laden der Journal-Daten');
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    if (!journalEnabled) {
      setOverview(null);
      setSyncRuns([]);
      setError(null);
      setLoading(false);
      return;
    }
    fetchJournalData();
  }, [journalEnabled, fetchJournalData]);

  if (!journalEnabled) {
    return (
      <div className="space-y-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
            <FontAwesomeIcon icon={faBook} className="w-6 h-6 mr-3" />
            Trading-Journal
          </h2>
          <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-400">
            Das Journal-Feature ist für diesen Benutzer deaktiviert. Es werden keine Journal-Daten geladen.
            Das Feature lässt sich im Tab „Features“ aktivieren.
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin" />
        <span className="ml-2 text-gray-600 dark:text-gray-400">Lade Journal-Daten...</span>
      </div>
    );
  }

  if (error !== null) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col items-center text-center py-6">
          <FontAwesomeIcon icon={faTriangleExclamation} className="w-8 h-8 text-red-600 dark:text-red-400 mb-4" />
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={fetchJournalData}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 cursor-pointer"
          >
            <FontAwesomeIcon icon={faRefresh} className="w-4 h-4" />
            <span>Erneut versuchen</span>
          </button>
        </div>
      </div>
    );
  }

  if (overview === null) {
    return null;
  }

  const ib = overview.ib;
  const hasAnyData =
    overview.optionTradeCount > 0 ||
    overview.stockPositionCount > 0 ||
    overview.tagCount > 0 ||
    overview.templateCount > 0 ||
    overview.reviewCount > 0 ||
    overview.noteCount > 0 ||
    ib.tokenSet ||
    ib.configured ||
    syncRuns.length > 0;

  if (!hasAnyData) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col items-center text-center py-8">
          <FontAwesomeIcon icon={faBook} className="w-8 h-8 text-gray-400 dark:text-gray-500 mb-4" />
          <p className="text-gray-900 dark:text-white font-medium mb-1">Noch keine Journal-Daten</p>
          <p className="text-gray-600 dark:text-gray-400 max-w-lg">
            Der Benutzer hat das Journal-Feature freigeschaltet, aber bisher keine Trades, Tags, Vorlagen oder
            Reviews angelegt und auch keine Interactive-Brokers-Verbindung eingerichtet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Usage */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
          <FontAwesomeIcon icon={faBook} className="w-6 h-6 mr-3" />
          Nutzung
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <StatTile
            label="Optionstrades"
            value={`${overview.optionTradeCount}`}
            hint={`${overview.optionTradeOpenCount} offen · ${overview.optionTradeClosedCount} geschlossen`}
          />
          <StatTile
            label="Aktienpositionen"
            value={`${overview.stockPositionCount}`}
            hint={`${overview.stockPositionOpenCount} offen`}
          />
          <StatTile
            label="Reviews"
            value={`${overview.reviewCount}`}
            hint={`${overview.reviewOpenCount} offen`}
          />
          <StatTile label="Notizen" value={`${overview.noteCount}`} hint="Trades/Positionen mit Notiz" />
          <StatTile label="Tags" value={`${overview.tagCount}`} />
          <StatTile label="Vorlagen" value={`${overview.templateCount}`} />
          <StatTile label="Manuell erfasst" value={`${overview.manualEntryCount}`} />
          <StatTile label="Importiert (IB)" value={`${overview.importedEntryCount}`} />
        </div>
      </div>

      {/* Key figures */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
          <FontAwesomeIcon icon={faChartLine} className="w-6 h-6 mr-3" />
          Kennzahlen
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatTile
            label="Realisierter G/V gesamt"
            value={formatCurrencyCentValue(overview.realizedPnLTotal, 'USD')}
            valueClass={pnlColorClass(overview.realizedPnLTotal)}
          />
          <StatTile label="Gebundenes Kapital" value={formatCurrencyCentValue(overview.capitalAtRisk, 'USD')} />
          <StatTile label="Trefferquote" value={formatPercentage(overview.winRate)} />
        </div>
      </div>

      {/* Activity */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
          <FontAwesomeIcon icon={faClock} className="w-6 h-6 mr-3" />
          Aktivität
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Field label="Erster Trade">
            {overview.firstTradeDate !== null ? formatRFCDate(overview.firstTradeDate) : 'noch nie'}
          </Field>
          <Field label="Letzter Trade">
            {overview.lastTradeDate !== null ? formatRFCDate(overview.lastTradeDate) : 'noch nie'}
          </Field>
          <Field label="Letzte Journal-Änderung">
            {overview.lastActivityAt !== null ? formatDate(overview.lastActivityAt) : 'noch nie'}
          </Field>
        </div>
      </div>

      {/* Interactive Brokers */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
          <FontAwesomeIcon icon={faPlug} className="w-6 h-6 mr-3" />
          Interactive Brokers
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Field label="Verbindung">
            <BooleanBadge value={ib.configured} trueLabel="Vollständig eingerichtet" falseLabel="Nicht eingerichtet" />
          </Field>

          <Field label="Flex-Token">
            <span className="inline-flex items-center">
              <FontAwesomeIcon icon={faKey} className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" />
              <BooleanBadge value={ib.tokenSet} trueLabel="Hinterlegt" falseLabel="Nicht hinterlegt" />
            </span>
          </Field>

          <Field label="Auto-Sync">
            <BooleanBadge value={ib.autoSync} trueLabel="Aktiv" falseLabel="Inaktiv" />
          </Field>

          <Field label="Flex Query (Trades)">
            <span className="font-mono">{ib.flexQueryTrades !== '' ? ib.flexQueryTrades : '—'}</span>
          </Field>

          <Field label="Erkannte Konten">
            {ib.accountIDs.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {ib.accountIDs.map((accountID) => (
                  <span
                    key={accountID}
                    className="inline-flex px-3 py-1 text-sm font-mono rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                  >
                    {accountID}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-gray-500 dark:text-gray-400">Keine</span>
            )}
          </Field>

          <Field label="Letzter Trade laut IB">
            {ib.lastTradeDate !== null ? formatRFCDate(ib.lastTradeDate) : 'noch nie'}
          </Field>

          <Field label="Letzte Synchronisation">
            {ib.lastSyncAt !== null ? formatDate(ib.lastSyncAt) : 'noch nie'}
          </Field>

          <Field label="Status">
            <SyncStatusBadge status={ib.lastSyncStatus} />
          </Field>

          <Field label="Manuell / Importiert">
            <span className="font-medium">
              {overview.manualEntryCount} manuell · {overview.importedEntryCount} importiert
            </span>
          </Field>
        </div>

        {ib.lastSyncMessage !== '' && (
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Letzte Meldung</div>
            <p className="text-gray-900 dark:text-white break-words">{ib.lastSyncMessage}</p>
          </div>
        )}
      </div>

      {/* Recent sync runs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
          <FontAwesomeIcon icon={faList} className="w-6 h-6 mr-3" />
          Letzte Synchronisationen
        </h2>

        {syncRuns.length === 0 ? (
          <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-400">
            Es wurde noch keine Synchronisation ausgeführt.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Start
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Auslöser
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Zeitraum
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ausführungen
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Trades
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Aktien
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ohne Symbol
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Meldung
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {syncRuns.map((run) => (
                  <tr key={run.id}>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                      {formatDate(run.startedAt)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      {SYNC_TRIGGER_LABELS[run.trigger]}
                      {run.fileName !== '' && (
                        <span className="block text-xs text-gray-500 dark:text-gray-400 font-mono">{run.fileName}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <SyncStatusBadge status={run.status} />
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      {syncRunPeriod(run)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                      {run.executionsFetched}
                      <span className="text-gray-500 dark:text-gray-400"> ({run.executionsNew} neu)</span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                      {run.optionTradesNew} neu
                      <span className="text-gray-500 dark:text-gray-400"> / {run.optionTradesUpdated} akt.</span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                      {run.stockPositionsNew} neu
                    </td>
                    <td className="px-4 py-2 text-sm whitespace-nowrap">
                      {run.unknownSymbols.length === 0 ? (
                        <span className="text-gray-500 dark:text-gray-400">—</span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                          {run.unknownSymbols.map((unknown) => unknown.ticker).join(', ')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 min-w-56 max-w-xs break-words">
                      {run.message !== '' ? run.message : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
